import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {BLUE, BLUE_LIGHT, GREEN, ORANGE, RED_LIGHT, WHITE} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

import Button, {ButtonSize, ButtonStyle} from './Button';

export enum PillStyle {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  CRITICAL = 'CRITICAL',
}

export enum PillSize {
  MINI = 'MINI',
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export interface PillProps {
  style: PillStyle;
  icon: BenchlingIconName;
  text: string;
  isDisabled?: boolean;
  size?: PillSize;
  onClick?: (e: ElementMouseEvent) => void;
}

export const PILL_STYLE_TO_BUTTON_STYLE_MAPPING: Record<PillStyle, ButtonStyle> = {
  [PillStyle.PRIMARY]: ButtonStyle.PRIMARY,
  [PillStyle.SECONDARY]: ButtonStyle.SECONDARY,
  [PillStyle.SUCCESS]: ButtonStyle.SUCCESS,
  [PillStyle.WARNING]: ButtonStyle.WARNING,
  [PillStyle.CRITICAL]: ButtonStyle.NEGATIVE,
};

export const PILL_SIZE_TO_BUTTON_SIZE_MAPPING: Record<PillSize, ButtonSize> = {
  [PillSize.MINI]: ButtonSize.MINI,
  [PillSize.SMALL]: ButtonSize.SMALL,
  [PillSize.MEDIUM]: ButtonSize.MEDIUM,
  [PillSize.LARGE]: ButtonSize.LARGE,
};

export default function Pill({
  style,
  icon,
  text,
  isDisabled = false,
  size = PillSize.MEDIUM,
  onClick,
}: PillProps): JSX.Element {
  const warningStyle = style === PillStyle.WARNING ? css(styles.warningStyle) : null;
  const pillClassName = classNames(css(styles.pill), css(styles[size]), warningStyle);
  if (onClick != null) {
    return (
      <Button
        disabled={isDisabled}
        size={PILL_SIZE_TO_BUTTON_SIZE_MAPPING[size]}
        icon={icon}
        text={text}
        buttonStyle={PILL_STYLE_TO_BUTTON_STYLE_MAPPING[style]}
        className={classNames(pillClassName, warningStyle)}
        onClick={onClick}
      />
    );
  }
  return (
    <div
      className={classNames(
        /**
         * Note: These "button-*" classes generally should be avoided. We use them here so that the div layout
         * matches the Button layout. A CSS-in-JS style is preferable to LESS style. This couples our code to
         * the Button implementation, which should be avoided.
         */
        `button--${PILL_SIZE_TO_BUTTON_SIZE_MAPPING[size]}`,
        'button--iconText',
        css(styles.nonActionablePill),
        css(styles[style]),
        pillClassName
      )}
    >
      <BenchlingIcon icon={icon} className={css(styles.icon)} />
      <span>{text}</span>
    </div>
  );
}

export const styles = StyleSheet.create({
  pill: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  nonActionablePill: {
    color: WHITE,
    cursor: 'default',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    marginBottom: '0px',
  },

  // use white text instead of default black text on orange for warnings
  warningStyle: {
    color: WHITE,
  },

  [PillSize.MINI]: {
    height: '22px',
    borderRadius: '11px',
    paddingLeft: '8px',
    paddingRight: '10px',
    fontSize: '13px',
  },
  [PillSize.SMALL]: {
    height: '24px',
    borderRadius: '12px',
    paddingLeft: '10px',
    paddingRight: '14px',
    fontSize: '13px',
  },
  [PillSize.MEDIUM]: {
    height: '28px',
    borderRadius: '14px',
    paddingLeft: '10px',
    paddingRight: '14px',
    fontSize: '13px',
  },
  [PillSize.LARGE]: {
    height: '34px',
    borderRadius: '17px',
    paddingLeft: '12px',
    paddingRight: '16px',
    fontSize: '15px',
  },

  [PillStyle.PRIMARY]: {
    background: BLUE_LIGHT,
  },
  [PillStyle.SECONDARY]: {
    background: BLUE,
  },
  [PillStyle.SUCCESS]: {
    background: GREEN,
  },
  [PillStyle.WARNING]: {
    background: ORANGE,
  },
  [PillStyle.CRITICAL]: {
    background: RED_LIGHT,
  },
});
